import {createSelector} from "@reduxjs/toolkit";

import {getAppraisalMethod} from "@reva/shared/indication-data";
import type {RevaIndicationEndpoint} from "@reva/shared/spec-conversion";

import {
    selectCombinedIndicationData,
    selectComparativeValueAvailable,
    selectIncomeValueAvailable,
    selectIndicationData,
    selectRealAssetValueAvailable,
    selectValueDataAvailable,
} from "./valueIndicationSlice";

/** True if any of the four indication methods produced a value. */
export const selectAnyValueAvailable = createSelector(
    [
        selectValueDataAvailable,
        selectIncomeValueAvailable,
        selectRealAssetValueAvailable,
        selectComparativeValueAvailable,
    ],
    (v, iv, rav, cv) => v || iv || rav || cv,
);

/**
 * Merge order of {@link selectFlattenedIndicationData} — a later endpoint overrides an earlier one
 * on a name collision. ENERGY_VALUE ranks lowest: its parameters are a by-product of the value
 * indication and must never shadow a value-indication parameter of the same name.
 */
const FLATTEN_ORDER: readonly RevaIndicationEndpoint[] = [
    "ENERGY_VALUE",
    "VALUE",
    "INCOME_VALUE",
    "REAL_ASSET_VALUE",
    "COMPARATIVE_VALUE",
];

/** All combined indication parameters of every method merged into one flat record, in {@link FLATTEN_ORDER}. */
export const selectFlattenedIndicationData = createSelector(
    [selectCombinedIndicationData],
    (combinedIndicationData) => {
        if (!combinedIndicationData) return null;

        return Object.fromEntries(
            FLATTEN_ORDER.flatMap((endpoint) => Object.entries(combinedIndicationData[endpoint]?.combined ?? {})),
        );
    },
);

/** The combined parameters of the method that matches the valuation's appraisal type. */
export const selectEffectiveIndicationData = createSelector(
    [selectIndicationData, selectCombinedIndicationData],
    (indicationData, combinedData) => {
        const key = getAppraisalMethod(indicationData?.indication_appraisal) ?? "VALUE";
        return combinedData?.[key]?.combined ?? null;
    },
);

const NO_SUGGESTIONS: readonly string[] = [];

/**
 * Eingabeparameter, für die die AVM einen alternativen Wert vorschlägt (`indication_suggestions`,
 * z.B. `market_adjustment_factor`, wenn der Sachwert deutlich unter dem Vergleichswert liegt).
 */
export const selectSuggestedParameters = createSelector([selectIndicationData], (indicationData) => {
    const suggestions = indicationData?.indication_suggestions;
    return suggestions && typeof suggestions === "object" ? Object.keys(suggestions) : NO_SUGGESTIONS;
});
